import { ALL_DATES, dateFilterSummary } from '@utils/cbDateSettings'
import { fmtDisplayDate } from '@utils/displayDate'
import { resolveFilterLabel } from '../reportGridUtils'

/**
 * The queue drawer's "Filters used" chips — moved out of `ReportJobDrawer.jsx` by spec
 * 167, which also taught them the report's own column names.
 *
 * ## Spec 167: a raw column id and CB's wire spelling of All Dates
 *
 * A generic run's `savedParams` is keyed by CB `columnId` (`jobs/routes.js`
 * `savedParamsOf`), so the drawer printed the KEY: run 4058d0f0 (report 190, schedule
 * 38bb1d8f) read **"1199: |"** on QA, 2026-10-12, while the schedule's editor read "All
 * Dates — rolls forward". Two defects in one chip:
 *
 *   1. `1199` is an id. Report 190's definition names that column "Entry Dates", and
 *      every filter surface prints it through `resolveFilterLabel` — the filter panel,
 *      the applied-criteria chips, the export header — which renders it "Date Picker"
 *      (Jordan, 2026-08-21, `reportGridUtils` FILTER_LABEL_ALIASES). The chip now asks
 *      that same producer, so it says what the report page says.
 *   2. `|` is All Dates as a STORED-PROC report sends it (`reportFilterWire.js`: BASIC
 *      reports send `$$cb_all`, proc reports a bare `|`, CC-49923), and
 *      `dateFilterSummary('|')` hands the bare pipe straight back. Both spellings now
 *      read "All Dates" — the preset's own name, as the schedule editor prints it.
 *
 * Both are gated on the DEFINITION, never on the value's shape. `$$cb_all` is also the
 * DROPDOWN "Select All" sentinel, and a TEXT filter can legitimately hold `|`; only a
 * column the definition declares `DATE` is read as a date. When the definition has not
 * arrived or could not be read there is no label to give, and every chip prints exactly
 * what it printed before — an un-decoded value is a reader's problem, a silently
 * reinterpreted one is ours.
 *
 * A choice filter (RADIO / DROPDOWN / CHECKBOX) names its keys from the definition's own
 * `filterConditions` too — "View By: Billed", not "61: 0" — but only when EVERY key it
 * carries is declared there; one unknown key prints the whole value as sent.
 *
 * **The READ is formatted and nothing else.** `job.savedParams` is exactly what "Run
 * again, same filters" replays, so it stays byte-identical; a re-run that quietly
 * re-scoped its own dates is CC-53051, and it shipped.
 */

/** A bare wire date, and each half of a wire range. Shape only — see `usDate`. */
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** The preset's own name for an unbounded date filter (`DATE_RANGE_PRESET_GROUPS`). */
export const ALL_DATES_LABEL = 'All Dates'

/** What a DROPDOWN's "Select All" sentinel means (`reportFilterWire.js`). */
const ALL_CHOICES_LABEL = 'All'

/** Choice filters whose wire keys the definition names in `filterConditions`. */
const CHOICE_TYPES = new Set(['RADIO', 'DROPDOWN', 'CHECKBOX'])

/**
 * MM/DD/YYYY, or '' if `iso` is not a real day on the calendar.
 *
 * The round-trip IS the guard, and it is not theoretical — measured 2026-08-27:
 * dayjs's lenient constructor ROLLS a nonexistent date forward instead of rejecting
 * it, so `fmtDisplayDate('2026-02-31')` returns **03/03/2026**, '2026-13-01' returns
 * 01/01/2027 and '0000-00-00' returns 11/30/1899. Rendering a date the run never
 * asked for, with nothing on screen saying so, is the one outcome worse than showing
 * the raw wire string. Reformatting the output back into the wire's own shape and
 * comparing catches exactly that, and needs no second date library here.
 */
const usDate = (iso) => {
  const out = fmtDisplayDate(iso)
  return out && `${out.slice(6)}-${out.slice(0, 2)}-${out.slice(3, 5)}` === iso ? out : ''
}

/**
 * Render ONE stored filter value for a human to read (Himaad UI feedback, 2026-08-27).
 *
 * "Filters used" printed the stored value verbatim, so a user read
 * `1199: 2026-08-01|2026-08-31` — CB's pipe-delimited wire range, in the one part of
 * this panel that is otherwise plain prose.
 *
 * The shape gate is the whole safety argument, and it is deliberately strict.
 * `savedParams` carries every filter a report has — ids, numbers, booleans, arrays —
 * so only a bare `YYYY-MM-DD`, or a pipe pair of them, is recognised; everything else
 * is stringified exactly as before. In particular `dateFilterSummary` must never be
 * handed a raw value: it reads '' and 0 as "All dates" and returns a non-date value
 * un-stringified, both of which are right for a resolved date filter and wrong for an
 * arbitrary param.
 *
 * Formatting delegates rather than reimplementing, so these pills read the same as the
 * report page's own filter pills, separator included (`cbDateSettings.dateFilterSummary`
 * is the one producer for that). The range case is gated on BOTH halves surviving
 * `usDate` before it is handed over, because that producer inherits the same rollover:
 * measured 2026-08-27, `dateFilterSummary('2026-02-31|2026-03-01')` answers
 * "03/03/2026 to 03/01/2026" — a backwards range of two dates nobody sent.
 *
 * Anything that fails any of those checks prints exactly as it did before.
 *
 * ⚠️ Superseded, kept so nobody restores it: this comment used to say `$$cb_all` is
 * "deliberately NOT translated". That still holds for a value with no column behind it
 * (this function never sees the definition); spec 167 translates it — and the bare `|`
 * — only where the definition says the column is a DATE (`chipValue` below).
 */
export const fmtSavedParam = (v) => {
  const raw = String(v)
  if (ISO_DATE.test(raw)) return usDate(raw) || raw
  const halves = raw.split('|')
  if (halves.length === 2 && halves.every((h) => ISO_DATE.test(h) && usDate(h))) return dateFilterSummary(raw)
  return raw
}

/** A choice filter's keys by their declared labels, or null if any key is undeclared. */
const choiceLabels = (raw, col) => {
  if (col.filterTypeName === 'DROPDOWN' && raw === ALL_DATES) return ALL_CHOICES_LABEL
  const keys = raw.split('|').map((k) => k.trim()).filter(Boolean)
  if (!keys.length) return null
  const conditions = Array.isArray(col.filterConditions) ? col.filterConditions : []
  const labels = keys.map((k) => conditions.find((c) => String(c?.key) === k)?.value)
  return labels.every((l) => l != null && String(l).trim() !== '') ? labels.join(', ') : null
}

/** One chip's value, read through its column when the definition supplies one. */
const chipValue = (v, col) => {
  const raw = String(v)
  if (col?.filterTypeName === 'DATE' && (raw === ALL_DATES || raw === '|')) return ALL_DATES_LABEL
  if (col && CHOICE_TYPES.has(col.filterTypeName)) {
    const named = choiceLabels(raw, col)
    if (named) return named
  }
  return fmtSavedParam(v)
}

/**
 * The chips for one run: `[{ key, label, value }]`, at most ten, in stored order.
 *
 * @param {object|null} savedParams the job's `savedParams` (unchanged by this)
 * @param {Array|null} columns the report definition's `columns`, or null when there is
 *   none (not read yet, unreadable, or not a generic report) — then every chip keeps
 *   its stored key and value exactly as before.
 */
export const savedFilterChips = (savedParams, columns = null) => {
  const cols = Array.isArray(columns) ? columns : []
  const byId = new Map(cols.map((c) => [String(c?.columnId), c]))
  return Object.entries(savedParams || {})
    .filter(([, v]) => v !== null && v !== '' && v !== false)
    .slice(0, 10)
    .map(([key, v]) => {
      const col = byId.get(String(key))
      const label = (col && resolveFilterLabel(col, cols)) || key
      return { key, label, value: chipValue(v, col) }
    })
}
